import React, { useEffect } from 'react';
import { Box, FormControl, Grid, MenuItem, Select } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { StepInfoContentWrapper } from '@/components/Health/HEPBuilder/steps/StepInformation/styles';
import { FactorCategory } from '@/components/Scan/Outcomes/types/FactorCategory.enum';
import { OutcomeCategory } from '@/components/Scan/Outcomes/types/OutcomeCategory.enum';
import { outcomeUsesSidedResults } from '@/components/Scan/Outcomes/utils';
import { FormRadioField, FormTextField } from '@/components/common/Form/Form';
import { FormFieldContainer } from '@/components/common/Form/styles';
import { useOutcomeBuilderContext } from '../state/hooks/OutcomeBuilderContext';

interface ResultDefinitionSelectProps {
    name: 'resultDefinitionId' | 'leftResultDefinitionId' | 'rightResultDefinitionId';
    label: string;
    control: Control<any>;
    resultDefinitions: { id: string; title: string }[];
}

const ResultDefinitionSelect = (props: ResultDefinitionSelectProps) => {
    const { name, label, control, resultDefinitions } = props;
    return (
        <FormFieldContainer width={320}>
            <h4>{label}</h4>
            <Controller
                name={name}
                control={control}
                render={({ field }) => {
                    return (
                        <FormControl variant="outlined">
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                MenuProps={{
                                    disablePortal: true,
                                }}
                                disabled
                            >
                                {resultDefinitions.map((rd) => {
                                    return (
                                        <MenuItem key={rd.id} value={rd.id}>
                                            {rd.title}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    );
                }}
            />
        </FormFieldContainer>
    );
};

export const OutcomeConfiguration = () => {
    const { control, errors, watch, setValue, assessment } = useOutcomeBuilderContext();

    const typeValue = watch('type');
    const tremorsScaleValue = watch('tremorsScale');
    const usesSidedResults = outcomeUsesSidedResults(typeValue, assessment);
    const resultDefinitions = Object.values(assessment.resultDefinitions);

    useEffect(() => {
        if (tremorsScaleValue && typeValue === OutcomeCategory.TREMORS) {
            if (tremorsScaleValue === 'UPDRS') {
                setValue('factors.0.category', FactorCategory.NORMAL);
                setValue('factors.1.category', FactorCategory.SLIGHT);
                setValue('factors.2.category', FactorCategory.MILD);
                setValue('factors.3.category', FactorCategory.MODERATE);
                setValue('factors.4.category', FactorCategory.SEVERE);
            }
            if (tremorsScaleValue === 'CRST') {
                setValue('factors.0.category', FactorCategory.NONE);
                setValue('factors.1.category', FactorCategory.SLIGHT);
                setValue('factors.2.category', FactorCategory.MODERATE);
                setValue('factors.3.category', FactorCategory.MARKED);
                setValue('factors.4.category', FactorCategory.SEVERE);
            }
        }
    }, [tremorsScaleValue, typeValue, setValue]);
    return (
        <div>
            <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item sm={10} md={7} lg={7} xl={7}>
                    <StepInfoContentWrapper>
                        <>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormFieldContainer width={320}>
                                    <h4>Risk Type</h4>
                                    <Controller
                                        name="type"
                                        control={control}
                                        render={({ field }) => {
                                            return (
                                                <FormControl variant="outlined">
                                                    <Select
                                                        {...field}
                                                        disabled
                                                        MenuProps={{
                                                            disablePortal: true,
                                                        }}
                                                    >
                                                        {Object.values(OutcomeCategory).map(
                                                            (option: OutcomeCategory) => {
                                                                return (
                                                                    <MenuItem key={option} value={option}>
                                                                        {option}
                                                                    </MenuItem>
                                                                );
                                                            },
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </FormFieldContainer>
                                {typeValue === OutcomeCategory.FALL && (
                                    <FormFieldContainer>
                                        <h4>Require Sex and Age Demographics</h4>
                                        <FormRadioField
                                            name="demographicsRequired"
                                            control={control}
                                            errors={errors}
                                            disabled
                                            label=""
                                            choices={[
                                                { value: 'true', label: 'Yes' },
                                                { value: 'false', label: 'No' },
                                            ]}
                                        />
                                    </FormFieldContainer>
                                )}
                            </Box>
                            {usesSidedResults ? (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <ResultDefinitionSelect
                                        name="leftResultDefinitionId"
                                        label="Left Result Definition"
                                        control={control}
                                        resultDefinitions={resultDefinitions}
                                    />
                                    <ResultDefinitionSelect
                                        name="rightResultDefinitionId"
                                        label="Right Result Definition"
                                        control={control}
                                        resultDefinitions={resultDefinitions}
                                    />
                                </Box>
                            ) : (
                                <ResultDefinitionSelect
                                    name="resultDefinitionId"
                                    label="Result Definition"
                                    control={control}
                                    resultDefinitions={resultDefinitions}
                                />
                            )}

                            {typeValue === OutcomeCategory.TREMORS && (
                                <FormFieldContainer width={320}>
                                    <h4>Tremors Scale</h4>
                                    <Controller
                                        name="tremorsScale"
                                        control={control}
                                        render={({ field }) => {
                                            return (
                                                <FormControl variant="outlined">
                                                    <Select
                                                        {...field}
                                                        MenuProps={{
                                                            disablePortal: true,
                                                        }}
                                                    >
                                                        <MenuItem value={'UPDRS'}>UPDRS</MenuItem>
                                                        <MenuItem value={'CRST'}>CRST</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </FormFieldContainer>
                            )}
                            <FormTextField
                                name="source"
                                control={control}
                                errors={errors}
                                autoFocus
                                margin="dense"
                                label="Source"
                                type="text"
                                fullWidth
                                error={!!errors.source}
                                helperText={errors.source?.message}
                                multiline
                                style={{ marginTop: 32 }}
                            />
                        </>
                    </StepInfoContentWrapper>
                </Grid>
            </Grid>
        </div>
    );
};
